import { useState, useEffect, useMemo, useCallback } from 'react'
import { Search, X } from 'lucide-react'
import { similarity } from '../../../utils/fuzzy'

/**
 * Editor for `type: object-ref` fields. Picks one or many
 * `builder_unit` nodes (object instances) of a given object type via
 * `/api/builder/search/objects?type=…`.
 *
 * The server returns every instance of the type with its enriched `data`
 * (capped at 500 ; the pilot deals with N ≪ 500). Filtering + ranking is
 * done client-side with a Levenshtein fuzzy match so approximate spellings
 * surface results even when a SQL LIKE wouldn't catch them.
 *
 * Value shape :
 *   - single  : {id, type, slug, label, data: {…}}
 *   - multiple: [{…}, …]
 *
 * The full enriched `data` is persisted in the saved value so the in-canvas
 * template can read `~.data.X` immediately after the user clicks "Save"
 * (without waiting for a server roundtrip). The server's `OrbitDataEnricher`
 * remains the source of truth on next page load — it overwrites whatever
 * the client cached.
 *
 * Props (passed via the modal field config) :
 *  - value      : current value (object / array / null)
 *  - objectType : machine name of the def to pick from (required)
 *  - multiple   : allow N selections (default false)
 *  - max        : optional cap when multiple
 *  - onSave, onClose : standard modal contract
 */
export const ObjectRefEditor = ({ value, objectType, multiple = false, max, onSave, onClose }) => {
  const [q, setQ] = useState('')
  const [allResults, setAllResults] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [selection, setSelection] = useState(() => normalizeIncoming(value, multiple))
  const [warning, setWarning] = useState(null)

  // Load every instance of the type once. Client-side ranking handles
  // filtering ; a server LIKE would defeat fuzzy matching by pruning the
  // candidate set before scoring.
  useEffect(() => {
    if (!objectType) {
      setError('Missing `objectType` in field config.')
      return
    }
    let cancelled = false
    setLoading(true)
    setError(null)
    fetch(`/api/builder/search/objects?type=${encodeURIComponent(objectType)}&limit=500`, {
      credentials: 'include',
    })
      .then(async (res) => {
        if (!res.ok) {
          if (res.status === 404) throw new Error(`Aucun type « ${objectType} » défini. Crée-le dans /builder/types.`)
          throw new Error(`HTTP ${res.status}`)
        }
        return res.json()
      })
      .then((data) => {
        if (cancelled) return
        setAllResults(Array.isArray(data) ? data : [])
      })
      .catch((e) => {
        if (cancelled) return
        setError(e.message || 'Search failed.')
        setAllResults([])
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [objectType])

  const selectedIds = useMemo(
    () => new Set(selection.map(s => s.id)),
    [selection],
  )

  // Rank results : query empty → original alphabetical order ; query set →
  // sort by composite score (substring match wins over fuzzy, fuzzy wins
  // over miss). Misses (similarity below threshold) are excluded.
  // Already-selected items are hidden from the picker — they're shown in
  // the left column and re-appear if the user removes them from there.
  const ranked = useMemo(() => {
    const available = allResults.filter(r => !selectedIds.has(r.id))
    const query = q.trim().toLowerCase()
    if (!query) return available
    const scored = available
      .map((row) => ({ row, score: scoreRow(row, query) }))
      .filter(({ score }) => score > 0)
    scored.sort((a, b) => b.score - a.score)
    return scored.map(({ row }) => row)
  }, [allResults, q, selectedIds])

  // Pick a result row. Already-picked rows are filtered out of the list so
  // this only ever appends (or replaces in single-select mode).
  const pick = useCallback((row) => {
    setWarning(null)
    if (!multiple) {
      setSelection([row])
      return
    }
    if (typeof max === 'number' && selection.length >= max) {
      setWarning(`Limite atteinte : ${max} sélection${max > 1 ? 's' : ''} maximum.`)
      return
    }
    setSelection(prev => [...prev, row])
  }, [multiple, max, selection.length])

  const moveSelection = useCallback((fromIndex, toIndex) => {
    setSelection(prev => {
      if (toIndex < 0 || toIndex >= prev.length) return prev
      const next = [...prev]
      const [picked] = next.splice(fromIndex, 1)
      next.splice(toIndex, 0, picked)
      return next
    })
  }, [])

  const removeAt = useCallback((index) => {
    setSelection(prev => prev.filter((_, i) => i !== index))
  }, [])

  const handleSave = () => {
    const payload = multiple ? selection : (selection[0] || null)
    onSave(payload)
    onClose()
  }

  const handleClear = () => {
    setSelection([])
    onSave(multiple ? [] : null)
    onClose()
  }

  const countLabel = multiple
    ? `${selection.length}${typeof max === 'number' ? ` / ${max}` : ''}`
    : ''

  return (
    <div className="flex flex-col gap-3">
      <div className="grid md:grid-cols-2 gap-4 min-h-[24rem]">
        {/* ── Colonne 1 : sélection courante ─────────────────────────────── */}
        <div className="flex flex-col gap-2 min-w-0">
          <div className="flex items-baseline justify-between">
            <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
              Sélection
            </span>
            {countLabel && (
              <span className="text-xs text-gray-400">{countLabel}</span>
            )}
          </div>

          <div className="flex-1 border border-gray-100 rounded-lg p-2 max-h-80 overflow-y-auto flex flex-col gap-1.5 bg-gray-50/40">
            {selection.length === 0 && (
              <p className="text-xs text-gray-400 text-center py-6">
                Aucune sélection.<br />
                Pioche dans la liste à droite.
              </p>
            )}
            {selection.map((s, i) => (
              <div
                key={`${s.id}-${i}`}
                className="flex items-center gap-2 p-2 bg-white border border-teal-200 rounded-lg shadow-sm"
              >
                {multiple && selection.length > 1 && (
                  <span className="flex flex-col text-teal-700">
                    <button
                      type="button"
                      onClick={() => moveSelection(i, i - 1)}
                      disabled={i === 0}
                      className="text-[10px] leading-none p-0.5 hover:bg-teal-100 disabled:opacity-30 rounded"
                      aria-label="Monter"
                      title="Monter"
                    >▲</button>
                    <button
                      type="button"
                      onClick={() => moveSelection(i, i + 1)}
                      disabled={i === selection.length - 1}
                      className="text-[10px] leading-none p-0.5 hover:bg-teal-100 disabled:opacity-30 rounded"
                      aria-label="Descendre"
                      title="Descendre"
                    >▼</button>
                  </span>
                )}
                <div className="flex-1 min-w-0 text-sm">
                  <div className="font-medium text-teal-900 truncate">{s.label || `#${s.id}`}</div>
                  <div className="text-xs text-teal-700 truncate">
                    id: {s.id}{s.slug ? ` · ${s.slug}` : ''}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => removeAt(i)}
                  className="p-1 rounded hover:bg-red-50 text-gray-400 hover:text-red-600 transition-colors"
                  aria-label="Retirer de la sélection"
                  title="Retirer"
                >
                  <X size={14} />
                </button>
              </div>
            ))}
          </div>

          {warning && (
            <div className="px-3 py-1.5 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg">
              {warning}
            </div>
          )}
        </div>

        {/* ── Colonne 2 : recherche + résultats ──────────────────────────── */}
        <div className="flex flex-col gap-2 min-w-0">
          <div className="relative">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={`Rechercher des « ${objectType} »…`}
              autoFocus
              className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-teal-500"
            />
          </div>

          {error && (
            <div className="px-3 py-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg">
              {error}
            </div>
          )}

          <div className="flex-1 border border-gray-100 rounded-lg max-h-80 overflow-y-auto bg-white">
            {loading && <p className="text-xs text-gray-400 text-center py-6">Chargement…</p>}
            {!loading && !error && ranked.length === 0 && (
              <p className="text-xs text-gray-400 text-center py-6">
                {q.trim()
                  ? 'Aucun résultat (essaie une orthographe approchée).'
                  : allResults.length > 0 && allResults.length === selection.length
                    ? 'Tout est déjà sélectionné.'
                    : 'Aucune instance disponible.'}
              </p>
            )}
            {!loading && ranked.map((r) => (
              <button
                key={r.id}
                type="button"
                onClick={() => pick(r)}
                className="w-full text-left px-3 py-2 text-sm transition-colors flex items-center justify-between border-b border-gray-50 last:border-b-0 hover:bg-gray-50"
              >
                <div className="min-w-0 flex-1">
                  <div className="truncate">{r.label || `#${r.id}`}</div>
                  <div className="text-xs text-gray-400 truncate">
                    id: {r.id}{r.slug ? ` · ${r.slug}` : ''}
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="builder-editor-actions builder-editor-actions-split">
        <button
          type="button"
          onClick={handleClear}
          className="btn btn-sm btn-ghost builder-editor-remove"
        >
          Tout vider
        </button>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={onClose}
            className="btn btn-secondary"
          >
            Annuler
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={multiple ? false : selection.length === 0}
            className="btn btn-primary"
          >
            Enregistrer
          </button>
        </div>
      </div>
    </div>
  )
}

// ─── Helpers ───────────────────────────────────────────────────────────────

/**
 * Coerce an incoming `value` into the internal `selection[]` shape used by
 * the editor. Preserves `data` so an existing selection's items remain
 * rendered correctly in the canvas right after the editor closes.
 */
function normalizeIncoming(value, multiple) {
  if (value == null || value === '') return []
  const arr = multiple
    ? (Array.isArray(value) ? value : [value])
    : (Array.isArray(value) ? value.slice(0, 1) : [value])
  return arr
    .map((v) => {
      if (!v || typeof v !== 'object' || v.id == null) return null
      return {
        id: Number(v.id),
        type: v.type,
        slug: v.slug,
        label: v.label,
        data: v.data,
      }
    })
    .filter(Boolean)
}

/**
 * Composite score for a row against a normalized lowercase query. Higher is
 * better. Returns 0 to exclude.
 *
 * Tiers (additive):
 *  - 100 + (label starts with query)
 *  -  50 + (label contains query)
 *  -  30 + (slug contains query)
 *  -  20 + (any data string field contains query)
 *  - levenshtein similarity on label only, scaled — kicks in for fuzzy
 *    matches that pure-substring misses ; threshold 0.55.
 */
function scoreRow(row, query) {
  const label = (row.label || '').toLowerCase()
  const slug = (row.slug || '').toLowerCase()
  let score = 0

  if (label.startsWith(query)) score += 100
  if (label.includes(query)) score += 50
  if (slug.includes(query)) score += 30

  // Cheap content match — scan known string fields of the enriched data.
  if (row.data && typeof row.data === 'object') {
    for (const v of Object.values(row.data)) {
      if (typeof v === 'string' && v.toLowerCase().includes(query)) {
        score += 20
        break
      }
    }
  }

  // Fuzzy on the label : surface approximate matches even when no substring
  // hit. Only counts above the 0.55 threshold (≈ 1 typo per 3 chars).
  const sim = similarity(label, query)
  if (sim >= 0.55) score += Math.round(sim * 40)

  return score
}

